import React from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Link as MUILink,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { ArrowUpward, Email, LocationOn, Phone } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { COMPANY, NAV_ITEMS, SOCIAL_LINKS } from '../../data/siteContent';
import { scrollToSection } from '../../utils/scroll';
import { openConsentPreferences } from '../../utils/consent';

const Footer = () => {
  const theme = useTheme();

  return (
    <Box
      component="footer"
      sx={{
        pt: { xs: 8, md: 10 },
        pb: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.background.paper} 45%)`,
        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(60rem 12rem at 20% -20%, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 60%),
                       radial-gradient(60rem 12rem at 80% -20%, ${alpha(theme.palette.secondary.main, 0.12)} 0%, transparent 60%)`,
          pointerEvents: 'none'
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Tooltip title="Back to top">
          <IconButton
            aria-label="Back to top"
            onClick={() => scrollToSection('home')}
            sx={{
              position: 'absolute',
              right: { xs: 0, md: 4 },
              top: { xs: -8, md: -12 },
              width: 44,
              height: 44,
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main',
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.22) }
            }}
          >
            <ArrowUpward />
          </IconButton>
        </Tooltip>

        <Grid container spacing={{ xs: 5, md: 6 }}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src="/favicon.png"
                alt=""
                width={40}
                height={40}
                sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'contain' }}
              />
              <Typography variant="h6" component="p" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.3 }}>
                {COMPANY.name}
              </Typography>
            </Box>

            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.8, maxWidth: 420 }}>
              Empowering Sri Lankan businesses with innovative software solutions since 2000.
            </Typography>

            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Email aria-hidden color="primary" fontSize="small" />
                <MUILink href={`mailto:${COMPANY.email}`} color="text.secondary">
                  {COMPANY.email}
                </MUILink>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Phone aria-hidden color="primary" fontSize="small" />
                <MUILink href={COMPANY.phoneHref} color="text.secondary">
                  {COMPANY.phone}
                </MUILink>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LocationOn aria-hidden color="primary" fontSize="small" />
                <Typography color="text.secondary">{COMPANY.location}</Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="overline" component="h2" sx={{ display: 'block', mb: 2, color: 'primary.main', fontSize: '0.72rem' }}>
              Quick Links
            </Typography>
            <Stack spacing={0.5} alignItems="flex-start">
              {NAV_ITEMS.map((item) => (
                <Button
                  key={item.id}
                  variant="text"
                  onClick={() => scrollToSection(item.id)}
                  sx={{
                    justifyContent: 'flex-start',
                    color: 'text.secondary',
                    fontWeight: 600,
                    px: 1,
                    ml: -1,
                    minHeight: 40,
                    '&:hover': { color: 'primary.main', backgroundColor: alpha(theme.palette.primary.main, 0.05) }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </Grid>

          <Grid size={{ xs: 6, md: 4 }}>
            <Typography variant="overline" component="h2" sx={{ display: 'block', mb: 2, color: 'primary.main', fontSize: '0.72rem' }}>
              Connect
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
              {SOCIAL_LINKS.map(({ icon: Icon, label, href, brand }) => (
                <Tooltip key={label} title={label}>
                  <IconButton
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${COMPANY.shortName} on ${label}`}
                    sx={{
                      width: 44,
                      height: 44,
                      backgroundColor: alpha(brand, 0.12),
                      color: brand,
                      transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
                      '&:hover': { backgroundColor: brand, color: '#fff', transform: 'translateY(-2px)' }
                    }}
                  >
                    <Icon />
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Business Hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {COMPANY.hours}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 4, md: 5 }, borderColor: alpha(theme.palette.primary.main, 0.12) }} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Typography color="text.secondary" variant="body2">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </Typography>

          <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              Established 2000 · 25+ Years of Excellence
            </Typography>
            <MUILink component={RouterLink} to="/privacy" color="text.secondary" variant="body2" sx={{ fontWeight: 600 }}>
              Privacy
            </MUILink>
            <MUILink component={RouterLink} to="/terms" color="text.secondary" variant="body2" sx={{ fontWeight: 600 }}>
              Terms
            </MUILink>
            {/* Consent must be as easy to withdraw as it was to give. */}
            <MUILink
              component="button"
              type="button"
              onClick={openConsentPreferences}
              color="text.secondary"
              variant="body2"
              sx={{ fontWeight: 600, background: 'none', border: 0, cursor: 'pointer', p: 0 }}
            >
              Cookie settings
            </MUILink>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
